import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getBtcgraphState } from '../services/minigameService';

const WS_ENDPOINTS = [
  'wss://stream.binance.com:9443/ws/btcusdt@trade',
  'wss://stream.binance.com:443/ws/btcusdt@trade',
  'wss://data-stream.binance.vision/ws/btcusdt@trade',
];

const CHART_SAMPLE_MS = 120;

export interface BtcPriceContextType {
  btcPrice: number;
  priceHistory: { time: number; price: number }[];
  setPriceHistory: React.Dispatch<React.SetStateAction<{ time: number; price: number }[]>>;
  btcPriceRef: React.MutableRefObject<number>;
  chartPriceRef: React.MutableRefObject<number>;
  lastChartSampleRef: React.MutableRefObject<number>;
  isConnected: boolean;
}

const BtcPriceContext = createContext<BtcPriceContextType | undefined>(undefined);

export function BtcPriceProvider({ children }: { children: React.ReactNode }) {
  const [btcPrice, setBtcPrice] = useState(0);
  const [priceHistory, setPriceHistory] = useState<{ time: number; price: number }[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const btcPriceRef = useRef<number>(0);
  const chartPriceRef = useRef<number>(0);
  const lastChartSampleRef = useRef<number>(0);

  // Run when site loads: fetch backend state to seed graph, then open WebSocket and keep it running
  useEffect(() => {
    getBtcgraphState()
      .then((state) => {
        const history = state.priceHistory ?? [];
        if (Array.isArray(history) && history.length > 0) {
          setPriceHistory(history);
        }
        const last = state.lastPrice;
        if (typeof last === 'number' && last > 0) {
          setBtcPrice(last);
          btcPriceRef.current = last;
          chartPriceRef.current = last;
        }
      })
      .catch(() => {});
  }, []);

  // WebSocket: connect on load, reconnect on close so it runs every time / continuously
  useEffect(() => {
    let idx = 0;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let ws: WebSocket | null = null;
    let unmounted = false;

    const connect = () => {
      if (unmounted) return;
      ws = new WebSocket(WS_ENDPOINTS[idx]);
      setIsConnected(false);

      ws.onopen = () => {
        if (unmounted) return;
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const price = parseFloat(data.p);
          const now = Date.now();
          btcPriceRef.current = price;
          chartPriceRef.current = price;
          setBtcPrice(price);

          if (now - lastChartSampleRef.current >= CHART_SAMPLE_MS) {
            lastChartSampleRef.current = now;
            setPriceHistory((prev) => {
              const cutoff = now - 60_000;
              return [...prev, { time: now, price }].filter((p) => p.time >= cutoff);
            });
          }
        } catch {
          /* ignore parse error */
        }
      };

      ws.onerror = () => {};

      ws.onclose = () => {
        if (unmounted) return;
        setIsConnected(false);
        timeout = setTimeout(() => {
          idx = (idx + 1) % WS_ENDPOINTS.length;
          connect();
        }, 2000);
      };
    };

    connect();

    return () => {
      unmounted = true;
      if (timeout) clearTimeout(timeout);
      if (ws) ws.close();
    };
  }, []);

  const value: BtcPriceContextType = {
    btcPrice,
    priceHistory,
    setPriceHistory,
    btcPriceRef,
    chartPriceRef,
    lastChartSampleRef,
    isConnected,
  };

  return (
    <BtcPriceContext.Provider value={value}>
      {children}
    </BtcPriceContext.Provider>
  );
}

export function useBtcPrice(): BtcPriceContextType {
  const ctx = useContext(BtcPriceContext);
  if (ctx === undefined) {
    throw new Error('useBtcPrice must be used within BtcPriceProvider');
  }
  return ctx;
}
